import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SELECT_VENUE_ROUTE } from '@constants/routes';
import { useAppLaunchDetection } from '@hooks/use-app-launch-detection';
import { getLastPathCookie } from '@utils/last-path-storage/last-path-storage';

/**
 * Component that handles last path restoration on app launch for Xceed Pro webview
 * It will redirect to the last visited path when the app is launched
 * Uses localStorage for persistence instead of cookies for better reliability
 */
export const LastPathPersistence: React.FC = () => {
  const { isInitialLoad, resetInitialLoad, startRestoration, endRestoration } = useAppLaunchDetection();
  const location = useLocation();

  useLayoutEffect(() => {
    // Only apply during initial load (any path, not just root paths)
    if (isInitialLoad) {
      // Add a small delay to ensure any pending localStorage operations are complete
      setTimeout(() => {
        // Get the last visited path from localStorage
        const lastPath = getLastPathCookie();

        // Reset the initial load state to prevent multiple redirects
        resetInitialLoad();

        // If we have a last path and it's different from the current path
        // Also navigate if we're at the select venue route (default route) and have a stored path
        if (lastPath && lastPath !== location.pathname && lastPath !== '/' && lastPath !== SELECT_VENUE_ROUTE) {
          // Start restoration to prevent navigation context from saving the path
          startRestoration();

          // Use window.location.replace to completely bypass the router
          // This prevents any router redirects from happening
          window.location.replace(lastPath);

          // Note: endRestoration won't be called because the page will reload
        } else {
          // End restoration since we're not navigating
          setTimeout(() => {
            endRestoration();
          }, 100);
        }
      }, 50); // Small delay to ensure localStorage operations are complete
    }
  }, [isInitialLoad, location.pathname, location.search, resetInitialLoad, startRestoration, endRestoration]);

  // This component doesn't render anything
  return null;
};
